import { config } from '../config';
import type { CapturePhase, MatchState } from '../types';
import { ok, reject, type DomainResult } from './result';

/**
 * SES-1 — the match state machine, and the §2 state vocabulary it defines.
 *
 * This module owns three things and nothing else:
 *
 * 1. **The §2 groupings.** `IN_PLAY_STATES` and `IN_MATCH_STATES` are declared here once, with
 *    narrowed types and type guards, so a consumer takes `InMatchState` in its signature and
 *    conflating the two groupings is a compile error rather than a discipline (design §2.1). The
 *    `no-restricted-syntax` rule in `eslint.config.js` refuses a bare `MatchState` string literal
 *    outside `src/domain/`, so a fourth grouping cannot be hand-rolled elsewhere.
 * 2. **The transition table** (design §2.2), as data. Every legal transition is a row; anything not
 *    in the table is illegal by construction rather than by an `else` branch someone can widen.
 * 3. **`transition()`** — a pure, total validator that returns the row operations a caller must
 *    apply, or a typed rejection. It never reads a clock and never performs I/O.
 *
 * **The anchor is the moment the press began (SES-1, revision 2).** The four clock-anchoring
 * transitions carry `anchorTs` captured at `pointerdown`; `transition` validates it against `now`
 * and never substitutes its own reading. A 700 ms hold therefore costs no timing accuracy, and a
 * stale or forged anchor cannot enter the clock.
 */

const MODULE = 'machine';

// ─────────────────────────────────────────────────────────────────────────────
// §2 — the state vocabulary
// ─────────────────────────────────────────────────────────────────────────────

/** All nine states, in lifecycle order. The runtime mirror of the `MatchState` type. */
export const MATCH_STATES = [
  'idle',
  'preMatch',
  'firstHalf',
  'halftime',
  'secondHalf',
  'fullTime',
  'triage',
  'distill',
  'archived',
] as const satisfies readonly MatchState[];

/**
 * Named access to each state, for the layers that may not spell one bare.
 *
 * The `ses-1/match-state-literals` lint rule refuses a `MatchState` value literal outside
 * `src/domain/`, so that nobody hand-rolls a fourth grouping out of strings. But `data/` legitimately
 * needs to *name* a state — a fixture row, a query on `matches.state`. `MATCH_STATE.firstHalf` is
 * strictly better than the literal it replaces: it is a checked reference to this module's
 * vocabulary, so a rename here breaks every consumer instead of silently leaving stale strings in
 * IndexedDB. Added by build unit U-3; the rule is unchanged.
 */
export const MATCH_STATE = {
  idle: 'idle',
  preMatch: 'preMatch',
  firstHalf: 'firstHalf',
  halftime: 'halftime',
  secondHalf: 'secondHalf',
  fullTime: 'fullTime',
  triage: 'triage',
  distill: 'distill',
  archived: 'archived',
} as const satisfies Record<MatchState, MatchState>;

/** Requirements §2 — "action is happening on the field and the user's attention belongs there". */
export const IN_PLAY_STATES = ['firstHalf', 'secondHalf'] as const satisfies readonly MatchState[];

/** Requirements §2 — "the match is underway and the phone is out and in use, **including the break**". */
export const IN_MATCH_STATES = [
  'firstHalf',
  'halftime',
  'secondHalf',
] as const satisfies readonly MatchState[];

export type InPlayState = (typeof IN_PLAY_STATES)[number];
export type InMatchState = (typeof IN_MATCH_STATES)[number];

/** Narrows to the in-play grouping. Capture surfaces, DISC-7/LIVE-12 reflow rules, LIVE-9. */
export function isInPlay(state: MatchState): state is InPlayState {
  return (IN_PLAY_STATES as readonly MatchState[]).includes(state);
}

/** Narrows to the in-match grouping. Wake lock (ENV-8), Abandon (SES-6), undo (LIVE-6), SES-9. */
export function isInMatch(state: MatchState): state is InMatchState {
  return (IN_MATCH_STATES as readonly MatchState[]).includes(state);
}

/**
 * Closes an exhaustive `switch` over a union. Adding a `MatchState` becomes a **compile error** at
 * every consumer that switches on it — which is what makes `deriveMatchMinute` total (INV-10).
 */
export function assertNever(value: never): never {
  throw new Error(`Unhandled domain variant: ${JSON.stringify(value)}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// SES-1 — the transition table (design §2.2)
// ─────────────────────────────────────────────────────────────────────────────

export type TransitionEvent =
  | 'CreateMatch'
  | 'DiscardSetup'
  | 'Kickoff'
  | 'Half'
  | 'Kickoff2H'
  | 'FullTime'
  | 'Abandon'
  | 'EnterTriage'
  | 'PassTriage'
  | 'CompleteDistillation'
  /**
   * B spec §6 / flag **F-1**, approved by Andrew 2026-08-13 — B's own `fullTime → archived` path
   * (build unit B-18). B never enters `triage`/`distill`, so before this event the only legal
   * route to `archived` ran through A's frozen distillation, and a B match could not be completed
   * at all. Two guards, both in `transition` below: zero unresolved flags and zero draft cards.
   */
  | 'CompleteMatch'
  /**
   * **Delete an abandoned match**, approved by Andrew 2026-08-19 from live use of the deployed app:
   * *"There should be a way to delete matches from the match history list. Like I just did a test
   * match and abandoned it and now I'd like it not to appear on the list."* Scope ruled in the same
   * breath: *"let's limit the scope to abandoned and never-started matches only as eligible for
   * deletion, and go ahead with the engine addition."*
   *
   * ── Why this is an added EVENT and not an added command ──────────────────────
   *
   * A second command that deletes a match would be a parallel variant of one that already works.
   * So this reaches the **same** delete effect `DiscardSetup` reaches: `to: null` →
   * `TransitionEffect.deletesMatch` → the single `cascadeDeleteMatch` op → `repos/cascade`. One
   * deletion path, one cascade, one place to get it wrong.
   *
   * **Citation note, and read it before re-deriving this shape.** B's requirements §5.6 governed
   * this decision when it was made, and it was **retired hours later** on 2026-08-19 with A —
   * this addition was the last one ruled under it. The permission machinery is gone; the reason is
   * not. §5's retirement notice keeps exactly this norm explicitly, *"as ordinary engineering
   * discipline rather than as a gate: do not clone engine logic into a near-duplicate variant and
   * call it new; reuse the existing capability or extend it, rather than growing a sibling beside
   * it."* So this shape stands on its own merit and is **not** owed to a clause that no longer
   * binds — which is the distinction this project has already had to correct six citations for.
   *
   * ── Why it is not `DiscardSetup` widened ─────────────────────────────────────
   *
   * `DiscardSetup` means *throw away an unstarted setup*, and it is ungated because a `preMatch`
   * match has nothing to protect. This one is gated on `abandoned`. Two windows and two guard sets
   * on one event name is the overloaded-term trap: the name would stop saying which rule applied.
   *
   * ── The "never started" half of the ruling needs nothing built ───────────────
   *
   * A never-started match is `preMatch`, and `DiscardSetup` already deletes it through this same
   * effect. It is also **not reachable from match history** — `domain/homeB.isMatchHistoryB`
   * excludes `preMatch` by name, so an unstarted match renders on the In-progress card instead.
   * Adding a `preMatch` row here would be a second event for a deletion that already has one.
   */
  | 'DeleteMatch'
  | 'Defer';

/** The four wall-clock anchors on the `Match` row (INV-7). */
export type AnchorField = 'kickoffTs' | 'halfTs' | 'kickoff2Ts' | 'fullTimeTs';

/**
 * Where an anchor's value comes from.
 *
 * `pressStart` is SES-1's confirming hold: the value is `anchorTs`, captured at `pointerdown`, and
 * is validated against `now`. `now` is Abandon, which writes `fullTimeTs = now` — it is not a
 * clock-anchoring transition in SES-1's sense and carries no hold (§2.2).
 */
export type AnchorSource = 'pressStart' | 'now';

export interface TransitionRow {
  readonly from: MatchState;
  readonly event: TransitionEvent;
  /** `null` means the match row is deleted rather than moved (DiscardSetup). */
  readonly to: MatchState | null;
  readonly anchor: { readonly field: AnchorField; readonly source: AnchorSource } | null;
  readonly setsAbandoned: boolean;
  readonly setsTriagePassed: boolean;
  readonly activeMatch: 'set' | 'clear' | 'unchanged';
  /** SYN-1 — the three push triggers: entering halftime, entering fullTime, completing distillation. */
  readonly outbox: boolean;
}

const NO_ANCHOR = null;

/**
 * The states `DeleteMatch` is offered from — **every state an abandoned match can rest in**.
 *
 * Derived, not chosen. Abandon lands in `fullTime`; from there the match can still be carried to
 * `triage` → `distill` (A's path, which INV-6 exempts an abandoned match from) or straight to
 * `archived` (B's `CompleteMatch`). `abandoned` is never cleared by any of those, so all four are
 * reachable with the flag set and each of them can render in match history.
 *
 * Everything else is absent, and each absence is the rule rather than a gap:
 *
 * - **`idle`, `preMatch`** — nothing here has been abandoned. `preMatch` deletes through
 *   `DiscardSetup`, which is where the ruling's "never started" half already lives.
 * - **`firstHalf`, `halftime`, `secondHalf`** — a live match is abandoned first (SES-6). Deleting a
 *   match out from under a running capture surface is not a product action, and its absence from
 *   this list makes it `ERR_ILLEGAL_TRANSITION` rather than something the `abandoned` guard has to
 *   catch (the flag cannot be true in these states anyway, so the guard alone would be a rule with
 *   no mechanism behind it).
 */
const DELETE_MATCH_FROM = ['fullTime', 'triage', 'distill', 'archived'] as const;

/**
 * Design §2.2, row for row. Order is lifecycle order, so the table reads as the match does.
 *
 * Anything absent is illegal: there is no `secondHalf → firstHalf`, no un-archive, no reverse for
 * any of the four anchoring transitions. SES-1 names that absence explicitly and it is the reason
 * the anchors need a confirming hold at all.
 */
export const TRANSITION_TABLE: readonly TransitionRow[] = [
  {
    from: 'idle',
    event: 'CreateMatch',
    to: 'preMatch',
    anchor: NO_ANCHOR,
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'set',
    outbox: false,
  },
  {
    from: 'preMatch',
    event: 'DiscardSetup',
    to: null,
    anchor: NO_ANCHOR,
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'clear',
    outbox: false,
  },
  {
    from: 'preMatch',
    event: 'Kickoff',
    to: 'firstHalf',
    anchor: { field: 'kickoffTs', source: 'pressStart' },
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'unchanged',
    outbox: false,
  },
  {
    from: 'firstHalf',
    event: 'Half',
    to: 'halftime',
    anchor: { field: 'halfTs', source: 'pressStart' },
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'unchanged',
    outbox: true,
  },
  {
    from: 'halftime',
    event: 'Kickoff2H',
    to: 'secondHalf',
    anchor: { field: 'kickoff2Ts', source: 'pressStart' },
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'unchanged',
    outbox: false,
  },
  {
    from: 'secondHalf',
    event: 'FullTime',
    to: 'fullTime',
    anchor: { field: 'fullTimeTs', source: 'pressStart' },
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'clear',
    outbox: true,
  },
  // SES-6 — Abandon is available from every in-match state, halftime included, and lands in
  // `fullTime` with `abandoned = true` so it fires SYN-1's push and reaches the backup service.
  ...(['firstHalf', 'halftime', 'secondHalf'] as const).map(
    (from): TransitionRow => ({
      from,
      event: 'Abandon',
      to: 'fullTime',
      anchor: { field: 'fullTimeTs', source: 'now' },
      setsAbandoned: true,
      setsTriagePassed: false,
      activeMatch: 'clear',
      outbox: true,
    }),
  ),
  {
    from: 'fullTime',
    event: 'EnterTriage',
    to: 'triage',
    anchor: NO_ANCHOR,
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'unchanged',
    outbox: false,
  },
  /**
   * B spec §6 (F-1) — the second exit from `fullTime`, beside `EnterTriage`.
   *
   * `outbox: false` **deliberately**. SYN-1 has three push triggers (entering halftime, entering
   * fullTime, completing distillation) and B's server backup is a requirements §4 non-goal, so
   * completing a B match adds no fourth. `outboxTriggerCoverage` compares this column against
   * `OUTBOX_TRIGGER_FOR` in both directions, so the absence is checked rather than assumed.
   *
   * `activeMatch: 'unchanged'` for `EnterTriage`'s reason: Full Time already cleared the pointer,
   * and writing a match id back at the moment it is archived would resurrect a resume target.
   */
  {
    from: 'fullTime',
    event: 'CompleteMatch',
    to: 'archived',
    anchor: NO_ANCHOR,
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'unchanged',
    outbox: false,
  },
  {
    from: 'triage',
    event: 'PassTriage',
    to: 'distill',
    anchor: NO_ANCHOR,
    setsAbandoned: false,
    setsTriagePassed: true,
    activeMatch: 'unchanged',
    outbox: false,
  },
  {
    from: 'distill',
    event: 'CompleteDistillation',
    to: 'archived',
    anchor: NO_ANCHOR,
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'unchanged',
    outbox: true,
  },
  {
    from: 'triage',
    event: 'Defer',
    to: 'fullTime',
    anchor: NO_ANCHOR,
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'unchanged',
    outbox: false,
  },
  {
    from: 'distill',
    event: 'Defer',
    to: 'fullTime',
    anchor: NO_ANCHOR,
    setsAbandoned: false,
    setsTriagePassed: false,
    activeMatch: 'unchanged',
    outbox: false,
  },
  // The `DeleteMatch` rows (Andrew, 2026-08-19). Last rather than in lifecycle position, because
  // this event is the one row set that spans the whole post-match tail rather than sitting at a
  // point in it — see DELETE_MATCH_FROM for which states, and `transition` for the guard that does
  // the eligibility work the table cannot do.
  ...DELETE_MATCH_FROM.map(
    (from): TransitionRow => ({
      from,
      event: 'DeleteMatch',
      to: null,
      anchor: NO_ANCHOR,
      setsAbandoned: false,
      setsTriagePassed: false,
      /*
       * **`unchanged`, and this is the one column where the obvious answer is wrong.**
       *
       * `DiscardSetup` clears the pointer because the `preMatch` match it deletes IS the pointer's
       * holder (§2.2). Here the opposite is true and the difference is a real defect: Abandon
       * already cleared the pointer on its way to `fullTime`, so by the time a match is deletable
       * the pointer either is null or names a DIFFERENT match — a new one created since, possibly
       * live. `clear` would wipe that match's resume target from a delete on an unrelated row.
       *
       * The residual is the damaged-data case (a pointer that somehow still names this row), and
       * it is already handled: `app/resume.resolveResume` logs a warning and lands on home when the
       * pointer names a match that no longer exists.
       */
      activeMatch: 'unchanged',
      /*
       * `outbox: false`. SYN-1 has exactly three push triggers (entering halftime, entering
       * fullTime, completing distillation) and a local delete is not a fourth — the same reasoning
       * `CompleteMatch` records above, checked in both directions by `outboxTriggerCoverage`.
       *
       * Stated cost, not an oversight: an abandoned match that already pushed at Abandon keeps its
       * copy on the backup service after this delete. The service exposes no delete route, and
       * adding one is a second engine change beyond what was authorised here.
       */
      outbox: false,
    }),
  ),
];

/** SES-1 — the transitions that write a wall-clock anchor from a confirming hold. */
export function requiresHold(row: TransitionRow): boolean {
  return row.anchor?.source === 'pressStart';
}

/** Looks a row up. Absence **is** the illegality rule — there is no second list to keep in sync. */
export function findTransition(
  from: MatchState,
  event: TransitionEvent,
): TransitionRow | undefined {
  return TRANSITION_TABLE.find((row) => row.from === from && row.event === event);
}

/**
 * Every state the table can actually reach from `idle`, computed rather than listed.
 *
 * INV-10's totality test enumerates *this*, so a state that becomes reachable later is covered
 * automatically and a state that never becomes reachable is not padded into the evidence.
 */
export function reachableStates(): MatchState[] {
  const seen = new Set<MatchState>(['idle']);
  const queue: MatchState[] = ['idle'];
  while (queue.length > 0) {
    const from = queue.shift() as MatchState;
    for (const row of TRANSITION_TABLE) {
      if (row.from !== from || row.to === null || seen.has(row.to)) continue;
      seen.add(row.to);
      queue.push(row.to);
    }
  }
  return MATCH_STATES.filter((s) => seen.has(s));
}

// ─────────────────────────────────────────────────────────────────────────────
// Guards and the transition function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Everything `transition` needs that is not the current state.
 *
 * Every field is required. An optional guard fact is a guard that silently does not run when the
 * caller forgets it, and these guards are the enforcement for SES-9, SES-5, DIST-1 and INV-6.
 * U-4 builds this from the snapshot read **inside** the transaction (design §1.1).
 */
export interface TransitionFacts {
  /** SES-9 — some OTHER match is in an in-match state. `preMatch` deliberately does not count. */
  readonly anotherMatchIsInMatch: boolean;
  /** Design §2.2 — an unstarted `preMatch` match already holds the `activeMatch` pointer. */
  readonly anotherPreMatchExists: boolean;
  /** SES-5 — this match's configured half length, validated at Kickoff. */
  readonly halfLengthMin: number;
  /** DIST-1 / F-1 — flag Observations with no `FlagResolution` row. Read once, guards two events. */
  readonly unresolvedFlagCount: number;
  /**
   * B §6 / F-1 — this match's `coachCards` rows still at `status:'draft'`.
   *
   * Required like every other fact here: an optional guard fact is a guard that silently does not
   * run, and this one is the difference between a draft the user can still finish and a draft
   * sealed behind `archived` with no window that would reopen it.
   */
  readonly draftCardCount: number;
  /** INV-6 — the ranks of this match's `phase=fullTime` TalkPoints. */
  readonly fullTimeTalkPointRanks: readonly number[];
  /** SES-6 — an abandoned match is exempt from INV-6. */
  readonly abandoned: boolean;
  /**
   * Carried, never read.
   *
   * VER-1 asks for the transition table "by capture phase". **No `Match.state` transition is gated
   * on `capturePhase`** — the gating runs the other way (`canLock` below). Threading the phase in
   * is what lets the VER-1 parity test drive the entire table twice, once under each phase, and
   * assert byte-identical results. A test that could not vary the input would prove nothing.
   */
  readonly capturePhase: CapturePhase;
}

export interface TransitionContext {
  /** Wall clock at the moment the handler runs. Supplied by the caller; the domain reads no clock. */
  readonly now: number;
  /** SES-1 — `pointerdown` time for the four anchoring transitions. Absent for every other event. */
  readonly anchorTs?: number;
  /** OBS-1 correlation. `null` for `CreateMatch`, where no match exists yet. */
  readonly matchId: string | null;
  readonly facts: TransitionFacts;
}

export interface TransitionEffect {
  readonly from: MatchState;
  readonly event: TransitionEvent;
  /** `null` when the match row is deleted (DiscardSetup). */
  readonly to: MatchState | null;
  readonly deletesMatch: boolean;
  readonly anchor: { readonly field: AnchorField; readonly value: number } | null;
  readonly setsAbandoned: boolean;
  readonly triagePassedTs: number | null;
  readonly activeMatch: 'set' | 'clear' | 'unchanged';
  /** SYN-1 — the caller writes an outbox row in the same transaction. */
  readonly outbox: boolean;
}

/**
 * SES-1 — validate a transition and return what to write. Pure, total, no I/O, no clock.
 *
 * Rejections are values carrying their own OBS-1 entry (see `result.ts`), so "rejection is
 * observable" is discharged here rather than delegated to a caller.
 *
 * **No anchor is written on a rejection** — the effect is built only after every guard has passed,
 * so there is no partial-write path to audit.
 */
export function transition(
  from: MatchState,
  event: TransitionEvent,
  ctx: TransitionContext,
): DomainResult<TransitionEffect> {
  const { now, anchorTs, matchId, facts } = ctx;

  const row = findTransition(from, event);
  if (row === undefined) {
    return reject(
      MODULE,
      matchId,
      'ERR_ILLEGAL_TRANSITION',
      `${event} is not a legal transition from ${from}.`,
      { from, event },
    );
  }

  // ── The anchor, first: it is the input that corrupts every later derived minute (SES-1) ──
  let anchorValue: number | null = null;
  if (row.anchor !== null) {
    if (row.anchor.source === 'now') {
      anchorValue = now;
    } else {
      if (anchorTs === undefined || !Number.isFinite(anchorTs)) {
        return reject(
          MODULE,
          matchId,
          'ERR_ANCHOR_REQUIRED',
          `${event} is a confirming-hold transition and must carry the press-start timestamp.`,
          { from, event },
        );
      }
      const age = now - anchorTs;
      const maxAge = config.TRANSITION_HOLD_MS + config.HOLD_SLOP_MS;
      if (age < 0 || age > maxAge) {
        return reject(
          MODULE,
          matchId,
          'ERR_ANCHOR_OUT_OF_RANGE',
          `The press-start timestamp for ${event} is ${age} ms old; only 0–${maxAge} ms is accepted.`,
          { from, event, ageMs: age, maxAgeMs: maxAge },
        );
      }
      anchorValue = anchorTs;
    }
  }

  // ── State guards (design §2.2's Guard column) ──
  if (event === 'CreateMatch') {
    if (facts.anotherMatchIsInMatch) {
      return reject(
        MODULE,
        matchId,
        'ERR_MATCH_IN_PROGRESS',
        'Another match is still in progress. Finish or abandon it first.',
        { from, event },
      );
    }
    if (facts.anotherPreMatchExists) {
      return reject(
        MODULE,
        matchId,
        'ERR_SETUP_IN_PROGRESS',
        'You already have an unstarted setup. Discard it before creating another.',
        { from, event },
      );
    }
  }

  if (event === 'Kickoff') {
    const { halfLengthMin } = facts;
    const min = config.HALF_LENGTH_MIN_MIN;
    const max = config.HALF_LENGTH_MIN_MAX;
    if (!Number.isInteger(halfLengthMin) || halfLengthMin < min || halfLengthMin > max) {
      return reject(
        MODULE,
        matchId,
        'ERR_HALF_LENGTH_RANGE',
        `Half length must be a whole number of minutes between ${min} and ${max}.`,
        { from, event, halfLengthMin },
      );
    }
  }

  /*
   * DIST-1 and F-1's first guard are **one rule with two events**, not two rules that agree.
   *
   * A's `PassTriage` and B's `CompleteMatch` both refuse to move a finished match past its flags
   * while any are untriaged; the spec's words for F-1 are *"the same predicate `PassTriage`
   * enforces, read through the same `listUnresolvedFlags` repo function — shared read, not a
   * cloned rule"*. Widening this condition is how that stays true: a second branch carrying the
   * same sentence is free to drift from this one, and nothing would report it.
   */
  if (
    (event === 'PassTriage' || event === 'CompleteMatch') &&
    facts.unresolvedFlagCount > 0
  ) {
    return reject(
      MODULE,
      matchId,
      'ERR_UNRESOLVED_FLAGS',
      `${facts.unresolvedFlagCount} flag(s) still need promoting or dismissing.`,
      { from, event, unresolvedFlagCount: facts.unresolvedFlagCount },
    );
  }

  /*
   * F-1's second guard (B §6). Every card window excludes `archived`, so completing over a draft
   * would strand it uneditable and undiscardable — the one state from which the user has no move.
   * Refused rather than repaired: finishing it and discarding it are different decisions, and both
   * are the user's.
   */
  if (event === 'CompleteMatch' && facts.draftCardCount > 0) {
    return reject(
      MODULE,
      matchId,
      'ERR_CARD_DRAFTS',
      `${facts.draftCardCount} card(s) are still drafts. Make the card or discard the draft first — a draft cannot be opened again once the match is complete.`,
      { from, event, draftCardCount: facts.draftCardCount },
    );
  }

  /*
   * **The whole of the delete eligibility rule, and it lives here rather than in the table.**
   *
   * Andrew's ruling admits *"abandoned and never-started matches only"*. A match that was genuinely
   * played to completion is not deletable — neither the one sitting at `fullTime` awaiting its card
   * nor the one already `archived`. And an abandoned match and a real match awaiting its card are
   * **both** in `fullTime`: `abandoned` is the only thing that tells them apart (there is no
   * `abandoned` member of `MatchState`; SES-6's Abandon lands in `fullTime` and sets the flag).
   *
   * `TRANSITION_TABLE` is keyed on `(from, event)` and cannot read a row field, so a table-only
   * answer would necessarily be wrong in one of the two directions — refusing the abandoned test
   * match the ruling is about, or offering to delete a real scouting record. The eligibility fact
   * therefore belongs where the other fact-guards are, over the snapshot the transaction opened
   * with (`app/bus.ts` reads `abandoned` from the `matches` row inside it), exactly as
   * `CompleteMatch` reads its flag and draft counts.
   *
   * This is the enforcement, and the Matches home's affordance is the courtesy — §2.2's standing
   * rule: *"the UI additionally hides illegal controls, but the domain rejection is the
   * enforcement."* A stale row that still shows the control gets refused here.
   */
  if (event === 'DeleteMatch' && !facts.abandoned) {
    return reject(
      MODULE,
      matchId,
      'ERR_MATCH_NOT_ABANDONED',
      'Only an abandoned match can be deleted. This one was played, so its record is kept.',
      { from, event },
    );
  }

  if (event === 'CompleteDistillation' && !facts.abandoned && !ranksAreComplete(facts)) {
    return reject(
      MODULE,
      matchId,
      'ERR_TALKPOINT_SET',
      `Archiving needs exactly ${config.TALKPOINT_COUNT} talk points ranked 1–${config.TALKPOINT_COUNT}.`,
      { from, event, ranks: [...facts.fullTimeTalkPointRanks] },
    );
  }

  return ok({
    from,
    event,
    to: row.to,
    deletesMatch: row.to === null,
    anchor: row.anchor === null || anchorValue === null ? null : { field: row.anchor.field, value: anchorValue },
    setsAbandoned: row.setsAbandoned,
    triagePassedTs: row.setsTriagePassed ? now : null,
    activeMatch: row.activeMatch,
    outbox: row.outbox,
  });
}

/** INV-6 — exactly `TALKPOINT_COUNT` `fullTime` TalkPoints, ranked `{1..TALKPOINT_COUNT}`, no dupes. */
function ranksAreComplete(facts: TransitionFacts): boolean {
  const ranks = new Set(facts.fullTimeTalkPointRanks);
  if (ranks.size !== config.TALKPOINT_COUNT) return false;
  if (facts.fullTimeTalkPointRanks.length !== config.TALKPOINT_COUNT) return false;
  for (let rank = 1; rank <= config.TALKPOINT_COUNT; rank++) {
    if (!ranks.has(rank)) return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// The capture phase (LOCK-4, PRE-7, LOCK-5)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * LOCK-4 / PRE-7 — may this match move `discovery → locked` right now?
 *
 * Gated on **state**, never the reverse: VER-1's "the table by capture phase" is answered by the
 * fact that no `Match.state` transition consults `capturePhase` at all.
 *
 * Two negatives live here, and both are INV-4:
 *   - **There is no `locked → discovery` transition.** LOCK-5 lets the user *visit* the Discovery
 *     surface from a locked board; that is navigation, not a phase change, and it assigns nothing.
 *   - **A second lock is refused**, so `boardPosition` cannot be assigned twice. `board.ts` refuses
 *     it a second time on the row data as well, because a UI guard is not an invariant.
 */
export function canLock(state: MatchState, phase: CapturePhase): boolean {
  if (phase === 'locked') return false;
  return state === 'preMatch' || isInMatch(state);
}
